/* eslint-disable @typescript-eslint/no-explicit-any */
export interface ApiResponse<T> {
    data?: T;
    error?: Error;
    status?: number;
    blob?: Blob;
}
export interface TokenResponse {
    access_token: string;
    refresh_token: string;
    token_type: string;
    meta: {
        role: string;
        permissions: string[];
    };
}

export class ApiWrapper {
    protected baseUrl: string;
    protected errorStatusCodes: number[] = [400, 401, 403, 404, 409, 422, 500];
    private applicationToken: string | null = null;

    constructor(baseUrl: string) {
        this.baseUrl = baseUrl;
    }

    protected async fetchApplicationToken(): Promise<TokenResponse> {
        const accessToken = window.sessionStorage.getItem("_mid-access-token");
        if (!accessToken) {
            throw new Error("Access token not found.");
        }

        try {
            const response = await fetch(`${this.baseUrl}/auth/access-token`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error("Failed to fetch application token.");
            }

            const data: TokenResponse = await response.json();
            this.applicationToken = data.access_token;
            return data;
        } catch (error) {
            console.error("Error fetching application token:", error);
            throw error;
        }
    }

    private async ensureApplicationToken(): Promise<void> {
        if (!this.applicationToken) {
            await this.fetchApplicationToken();
        }
    }

    private async fetchWithAuth<T>(endpoint: string, method: string, body?: any): Promise<ApiResponse<T>> {
        try {
            await this.ensureApplicationToken();

            const response = await fetch(`${this.baseUrl}${endpoint}`, {
                method,
                headers: {
                    Authorization: `Bearer ${this.applicationToken}`,
                    ...(body ? { "Content-Type": "application/json" } : {}),
                },
                redirect: "follow",
                body: body ? JSON.stringify(body) : undefined,
            });

            if (!response.ok) {
                if (this.errorStatusCodes.includes(response.status)) {
                    return { status: response.status };
                }
                const errorResponse = await response.json();
                const errorDetail = errorResponse?.detail || "Unknown error";
                throw new Error(errorDetail);
            }

            const data = await response.json();
            return { data, status: response.status };
        } catch (error) {
            return { error: error as Error };
        }
    }

    private async fetchBlobWithAuth(endpoint: string, method: string, body?: any): Promise<ApiResponse<Blob>> {
        try {
            await this.ensureApplicationToken();

            const response = await fetch(`${this.baseUrl}${endpoint}`, {
                method,
                headers: {
                    Authorization: `Bearer ${this.applicationToken}`,
                    ...(body ? { "Content-Type": "application/json" } : {}),
                },
                redirect: "follow",
                body: body ? JSON.stringify(body) : undefined,
            });

            // Check for valid status code
            if (!response.ok) {
                if (this.errorStatusCodes.includes(response.status)) {
                    return { status: response.status };
                }
                const errorResponse = await response.json();
                const errorDetail = errorResponse?.detail || "Unknown error";
                throw new Error(errorDetail);
            }

            const blob = await response.blob();
            return { blob, status: response.status }; // Return the blob data without converting it to JSON
        } catch (error) {
            return { error: error as Error };
        }
    }

    protected async getBlob(endpoint: string): Promise<ApiResponse<Blob>> {
        return this.fetchBlobWithAuth(endpoint, "GET");
    }

    protected async postBlob(endpoint: string, body: any): Promise<ApiResponse<Blob>> {
        return this.fetchBlobWithAuth(endpoint, "POST", body);
    }

    protected async get<T>(endpoint: string): Promise<ApiResponse<T>> {
        return this.fetchWithAuth<T>(endpoint, "GET");
    }

    protected async post<T>(endpoint: string, body: any): Promise<ApiResponse<T>> {
        return this.fetchWithAuth<T>(endpoint, "POST", body);
    }

    protected async patch<T>(endpoint: string, body: any): Promise<ApiResponse<T>> {
        return this.fetchWithAuth<T>(endpoint, "PATCH", body);
    }

    protected async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
        return this.fetchWithAuth<T>(endpoint, "DELETE");
    }

    protected async put<T>(endpoint: string, body: any): Promise<ApiResponse<T>> {
        return this.fetchWithAuth<T>(endpoint, "PUT", body);
    }
}
